// React Query options + panel status for the lazily fetched GDACS footprint. Pure: the
// fetch implementation is injected so it can run under node --test.
import { parseGdacsGeometry, GdacsParseError } from './gdacsGeometry.js';
import { parseGdacsIsoUtc } from './gdacsTime.js';

// Only the public feed's geometry endpoint is ever fetched; anything else (other host, http, relative) is ignored.
const GEOMETRY_HOST = 'www.gdacs.org';
const GEOMETRY_PATH = '/gdacsapi/api/polygons/getgeometry';
export function isAllowedGeometryUrl(value) {
  try {
    const u = new URL(String(value));
    return u.protocol === 'https:' && u.hostname === GEOMETRY_HOST && !u.port && !u.username && !u.password && u.pathname === GEOMETRY_PATH;
  } catch {
    return false;
  }
}

export function geometryQueryOptions(event, { fetchImpl = globalThis.fetch } = {}) {
  const rawUrl = event?.urls?.geometry ?? null;
  const url = isAllowedGeometryUrl(rawUrl) ? rawUrl : null;
  const id = event?.id != null ? String(event.id) : null;
  return {
    queryKey: ['gdacsGeometry', id],
    enabled: !!url,
    queryFn: async ({ signal } = {}) => {
      const parseOpts = {
        eventType: event.eventType,
        fromDate: event.fromDate,
        toDate: event.toDate,
        fallbackCentroid: [event.lng, event.lat],
      };
      const r = await fetchImpl(url, { signal, redirect: 'error', credentials: 'omit' });
      if (!r.ok) throw new Error(`GDACS geometry ${r.status}`);
      const fc = await r.json();
      const parsed = parseGdacsGeometry(fc, parseOpts);
      parsed.sourceUrl = url;
      parsed.eventId = id;
      parsed.fallbackModifiedMs = parseGdacsIsoUtc(event.dateModified);   // stored with the data: a later advisory must not relabel it
      return parsed;
    },
    staleTime: 0,                       // every open revalidates; a new advisory is picked up by the URL change
    gcTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: (count, err) => !(err instanceof GdacsParseError) && count < 1,
  };
}

// Data belongs to the selected event (keyed by event id); a footprint of another event never counts.
const ownData = (event, query) =>
  (query?.data && String(query.data.eventId) === String(event?.id) ? query.data : null);

// idle | unavailable | loading | error | ready. A failed REFETCH keeps the path on screen ('ready').
export function derivePanelStatus({ event, query }) {
  if (!event) return 'idle';
  if (!isAllowedGeometryUrl(event.urls?.geometry)) return 'unavailable';
  if (ownData(event, query)) return 'ready';
  if (query?.status === 'error') return 'error';
  return 'loading';
}

// -> { refetchFailed }: the last refresh failed and the previous path is still shown.
export function pathMeta({ query, event }) {
  const data = ownData(event, query);
  return { refetchFailed: !!data && !!query?.error };
}

// When the path was last updated: the footprint's own timestamp, else the one captured with it. Never the selected event's.
export const pathUpdatedMsOf = (parsed) => parsed?.modifiedMs ?? parsed?.fallbackModifiedMs ?? null;
